import { Pencil, Trash2, Printer, MapPin, Tag, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import type { ProductionLocation } from "@/hooks/useProductionLocations";

const JOB_TYPE_LABELS: Record<string, string> = {
  kitchen: "Cozinha",
  bar: "Bar",
  drinks: "Drinks",
  portions: "Porções",
  order_production: "Produção Geral",
};

interface ProductionLocationListProps {
  locations: ProductionLocation[];
  isLoading: boolean;
  onEdit: (location: ProductionLocation) => void;
  onDelete: (id: string) => void;
  onToggleActive: (id: string) => void;
  onDuplicate: (location: ProductionLocation) => void;
}

export function ProductionLocationList({
  locations,
  isLoading,
  onEdit,
  onDelete,
  onToggleActive,
  onDuplicate,
}: ProductionLocationListProps) {
  if (isLoading) {
    return (
      <div className="rounded-lg border bg-card">
        <div className="p-4 space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-4">
              <Skeleton className="h-10 w-10 rounded-full" />
              <Skeleton className="h-4 flex-1" />
              <Skeleton className="h-8 w-20" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (locations.length === 0) {
    return (
      <div className="rounded-lg border bg-card p-8 text-center">
        <MapPin className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium">Nenhum local de produção</h3>
        <p className="text-muted-foreground">
          Adicione locais como Cozinha, Bar, Copa para direcionar os pedidos
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Local</TableHead>
            <TableHead>Tipo</TableHead>
            <TableHead>Impressora</TableHead>
            <TableHead className="text-center">Ativo</TableHead>
            <TableHead className="text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {locations.map((location) => (
            <TableRow key={location.id}>
              <TableCell>
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-primary" />
                  <span className="font-medium">{location.name}</span>
                </div>
              </TableCell>
              <TableCell>
                {location.job_type ? (
                  <Badge variant="secondary" className="gap-1">
                    <Tag className="h-3 w-3" />
                    {JOB_TYPE_LABELS[location.job_type] || location.job_type}
                  </Badge>
                ) : (
                  <span className="text-muted-foreground text-sm">—</span>
                )}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Printer className="h-4 w-4 text-muted-foreground" />
                  <div className="flex flex-col">
                    <span className="font-medium text-sm">
                      {location.printers?.name || "—"}
                    </span>
                    <code className="text-xs text-muted-foreground">
                      {location.printers?.printer_name || location.printer_name}
                    </code>
                  </div>
                </div>
              </TableCell>
              <TableCell className="text-center">
                <Switch
                  checked={location.is_active}
                  onCheckedChange={() => onToggleActive(location.id)}
                />
              </TableCell>
              <TableCell className="text-right">
                <TooltipProvider delayDuration={300}>
                  <div className="flex justify-end gap-1">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onDuplicate(location)}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Duplicar local</p>
                      </TooltipContent>
                    </Tooltip>

                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onEdit(location)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Editar</p>
                      </TooltipContent>
                    </Tooltip>

                    <AlertDialog>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </AlertDialogTrigger>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Excluir</p>
                        </TooltipContent>
                      </Tooltip>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Excluir local de produção?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Esta ação não pode ser desfeita. O local "{location.name}" será removido permanentemente.
                            Produtos associados a este local perderão a configuração de impressão.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => onDelete(location.id)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            Excluir
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </TooltipProvider>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
